import React, { useMemo, useState, useCallback } from "react";

/**
 * MathCrossword — fixed 50px cells (no rounding surprises)
 * - Grid columns are set inline: repeat(cols, 50px)
 * - Each cell gets inline width/height: 50px
 * - No CSS rule sets grid-template-columns (avoids accidental overrides)
 */

const CELL = 50; // hard size in px

/* ---------------- helpers ---------------- */

const OP_MAP = { "×": "*", "÷": "/" };

const OP_ALIASES = {
  "+": "+", "＋": "+",
  "-": "-", "−": "-", "–": "-", "—": "-",
  "*": "*", x: "*", "×": "*",
  "/": "/", "÷": "/",
  "%": "%",
};
const EQ_ALIASES = { "=": true };

function normalizeToken(t) {
  if (t == null) return "";
  if (typeof t === "number") return String(t);
  const s = String(t).trim();
  if (OP_ALIASES[s]) return OP_ALIASES[s];
  if (EQ_ALIASES[s]) return "=";
  return s;
}
const isNumber = (t) => /^\d+$/.test(normalizeToken(t));
const isOp = (t) => ["+", "-", "*", "/", "%"].includes(normalizeToken(t));
const isEq = (t) => normalizeToken(t) === "=";

function applyOp(a, op, b) {
  const A = Number(a), B = Number(b);
  switch (normalizeToken(op)) {
    case "+": return A + B;
    case "-": return A - B;
    case "*": return A * B;
    case "/": return B === 0 ? NaN : A / B;
    case "%": return B === 0 ? NaN : A % B;
    default: return NaN;
  }
}

const EPS = 1e-9;
const equal = (x, y) => Math.abs(Number(x) - Number(y)) <= EPS;

/** Evaluate a [A, op, B, '=', C] segment: returns { ok: true|false|null } */
function checkSegment(tokens) {
  if (!Array.isArray(tokens) || tokens.length !== 5) return { ok: false };
  let [a, op, b, eq, c] = tokens.map(normalizeToken);
  if (!isEq(eq)) return { ok: false };

  const blanks = [a, op, b, c].filter((t) => t === "_").length;
  if (blanks >= 2) return { ok: null };

  if (blanks === 0) {
    if (!(isNumber(a) && isOp(op) && isNumber(b) && isNumber(c))) return { ok: false };
    const left = applyOp(a, op, b);
    return { ok: Number.isFinite(left) && equal(left, c) };
  }

  if (op === "_") return { ok: null }; // can’t deduce unknown operator with one blank elsewhere

  // with one blank but not operator, we can't fully validate => unknown
  return { ok: null };
}

/** Scan a line and collect segment validity (for per-cell flags) */
function scanLine(values) {
  const segs = [];
  for (let i = 0; i <= values.length - 5; i++) {
    const slice = values.slice(i, i + 5);
    if (slice.some((t) => t === " ")) continue;
    segs.push({ i, ok: checkSegment(slice).ok });
  }
  return segs;
}

function deepCloneGrid(g) {
  return g.map((r) => r.slice());
}

/** Build default toolbox from level */
function buildDefaultPalette(level) {
  const nums = new Set();
  const ops = new Set();

  (level?.grid ?? []).forEach((row) => {
    row.forEach((t) => {
      t = normalizeToken(t);
      if (isNumber(t)) nums.add(t);
      if (isOp(t)) ops.add(t);
    });
  });

  // add a few more numbers/operators
  ["3", "5", "9", "10", "14", "13", "20", "25", "30", "45", "60", "72", "89", "289", "369"]
    .forEach((n) => nums.add(n));
  ["+", "-", "×", "÷"].forEach((o) => ops.add(o));

  return { numbers: Array.from(nums), operators: Array.from(ops) };
}

/** Fill the single "_" in [A, op, B, "=", C]; returns string or null */
function fillMissing(tokens) {
  if (!Array.isArray(tokens) || tokens.length !== 5) return null;

  const [rawA, rawOp, rawB, rawEq, rawC] = tokens;
  const A  = normalizeToken(rawA);
  const op = normalizeToken(rawOp);
  const B  = normalizeToken(rawB);
  const eq = normalizeToken(rawEq);
  const C  = normalizeToken(rawC);

  if (!isEq(eq) || !isOp(op)) return null;

  const toNum = (x) => Number(x);
  const missingIdx = [A, op, B, eq, C].findIndex((t) => t === "_");
  if (missingIdx === -1) return null;

  try {
    // Missing left operand
    if (missingIdx === 0 && isNumber(B) && isNumber(C)) {
      const b = toNum(B), c = toNum(C);
      switch (op) {
        case "+": return String(c - b);
        case "-": return String(c + b);
        case "*": return b === 0 ? null : String(c / b);
        case "/": return String(c * b);
        case "%": return null;
        default:  return null;
      }
    }
    // Missing right operand
    if (missingIdx === 2 && isNumber(A) && isNumber(C)) {
      const a = toNum(A), c = toNum(C);
      switch (op) {
        case "+": return String(c - a);
        case "-": return String(a - c);
        case "*": return a === 0 ? null : String(c / a);
        case "/": return String(a / c);
        case "%": return null;
        default:  return null;
      }
    }
    // Missing result
    if (missingIdx === 4 && isNumber(A) && isNumber(B)) {
      const a = toNum(A), b = toNum(B);
      switch (op) {
        case "+": return String(a + b);
        case "-": return String(a - b);
        case "*": return String(a * b);
        case "/": return b === 0 ? null : String(a / b);
        case "%": return b === 0 ? null : String(a % b);
        default:  return null;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

/* ---------------- component ---------------- */

export default function MathCrossword1({
  level = SAMPLE_LEVEL,
  palette,
  singleUse = false,
  onFinish,
}) {
  const initial = useMemo(() => deepCloneGrid(level.grid), [level]);
  const inputMask = useMemo(
    () => level.grid.map((row) => row.map((t) => t === "_" )),
    [level]
  );

  const [grid, setGrid] = useState(initial);
  const [lives, setLives] = useState(5);
  const [usedBag, setUsedBag] = useState({});
  const [touchValue, setTouchValue] = useState(null);

  const toolbox = useMemo(() => {
    const base = buildDefaultPalette(level);
    return {
      numbers: (palette?.numbers ?? base.numbers).map(normalizeToken),
      operators: palette?.operators ?? base.operators,
    };
  }, [level, palette]);

  const totals = useMemo(() => {
    let total = 0, filled = 0;
    grid.forEach((r, ri) =>
      r.forEach((t, ci) => {
        if (inputMask[ri][ci]) {
          total++;
          if (t && t !== "_") filled++;
        }
      })
    );
    return { total, filled };
  }, [grid, inputMask]);

  const validation = useMemo(() => {
    const rowFlags = grid.map((r) => r.map(() => true));

    // rows only (that’s enough for this level layout)
    grid.forEach((row, r) => {
      const segs = scanLine(row);
      segs.forEach((seg) => {
        for (let k = 0; k < 5; k++) {
          rowFlags[r][seg.i + k] = rowFlags[r][seg.i + k] && seg.ok;
        }
      });
    });

    const flags = grid.map((row, r) => row.map((t, c) => rowFlags[r][c]));

    const solved =
      totals.filled === totals.total &&
      grid.every((row, r) =>
        row.every((t, c) => {
          if (!inputMask[r][c]) return true;
          return flags[r][c];
        })
      );

    return { flags, solved };
  }, [grid, totals, inputMask]);

  const setCell = useCallback(
    (r, c, value) => {
      value = normalizeToken(value);
      if (!inputMask[r][c]) return;
      if (value === "") value = "_";
      setGrid((prev) => {
        const next = deepCloneGrid(prev);
        next[r][c] = value;
        return next;
      });
    },
    [inputMask]
  );

  const handleDrop = (r, c, e) => {
    const v = e.dataTransfer.getData("text/plain");
    if (!v) return;
    if (singleUse) {
      setUsedBag((b) => ({ ...b, [v]: (b[v] ?? 0) + 1 }));
    }
    setCell(r, c, v);
  };

  const handleToolPick = (v) => setTouchValue(v);

  const handleCellClick = (r, c) => {
    if (!inputMask[r][c]) return;
    if (touchValue != null) {
      setCell(r, c, touchValue);
      if (singleUse)
        setUsedBag((b) => ({ ...b, [touchValue]: (b[touchValue] ?? 0) + 1 }));
    }
  };

  // Hint: fill one deducible blank, consume a life
  const giveHint = () => {
    for (let r = 0; r < grid.length; r++) {
      for (let i = 0; i <= grid[r].length - 5; i++) {
        const slice = grid[r].slice(i, i + 5);
        if (slice.includes(" ")) continue;
        const blanks = slice.reduce(
          (acc, t, idx) => (t === "_" ? acc.concat(idx) : acc),
          []
        );
        if (blanks.length === 1) {
          const bi = blanks[0];
          const filled = fillMissing(slice);
          if (filled != null) {
            const targetCol = i + bi;
            setCell(r, targetCol, filled);
            setLives((l) => Math.max(0, l - 1));
            return;
          }
        }
      }
    }
  };

  React.useEffect(() => {
    if (validation.solved && onFinish) {
      onFinish({ correct: true, filled: totals.filled, total: totals.total });
    }
  }, [validation.solved, totals, onFinish]);

  return (
    <div className="mcw">
      <Header lives={lives} onHint={giveHint} />
      <div className="mcw-wrap">
        <Grid
          grid={grid}
          flags={validation.flags}
          mask={inputMask}
          onDrop={handleDrop}
          onSet={setCell}
          onCellClick={handleCellClick}
        />
        <Toolbox
          toolbox={toolbox}
          usedBag={usedBag}
          singleUse={singleUse}
          onPick={handleToolPick}
        />
      </div>
      <FooterStatus totals={totals} solved={validation.solved} />
    </div>
  );
}

/* ---------------- sub components ---------------- */

function Header({ lives, onHint }) {
  return (
    <div className="mcw-header">
      <button className="mcw-btn" onClick={onHint} title="Reveal one cell">
        💡 Hint
      </button>
      <div className="mcw-lives" title="Lives remaining">
        <span className="mcw-badge">{lives}</span> lives
      </div>
    </div>
  );
}

function Grid({ grid, flags, mask, onDrop, onSet, onCellClick }) {
  const cols = grid[0].length;

  return (
    <div
      className="mcw-grid"
      style={{ gridTemplateColumns: `repeat(${cols}, ${CELL}px)` }} // <- **50px locked**
    >
      {grid.map((row, r) =>
        row.map((t, c) => {
          const token = normalizeToken(t);
          const isWall  = token === " ";
          const isInput = mask[r][c];
          const isFixed = !isInput && token !== "_" && token !== " ";
          const ok = flags[r][c];

          const className = [
            "mcw-cell",
            isWall && "mcw-wall",
            isFixed && "mcw-fixed",
            isInput && "mcw-input",
            isOp(token) && "mcw-op",
            isEq(token) && "mcw-eq",
            (isFixed || (isInput && token !== "_")) && (ok ? "mcw-ok" : "mcw-bad"),
          ].filter(Boolean).join(" ");

          if (isWall) {
            return (
              <div
                key={`${r}-${c}`}
                className={className}
                style={{ width: `${CELL}px`, height: `${CELL}px` }}
              />
            );
          }

          return (
            <div
              key={`${r}-${c}`}
              className={className}
              style={{ width: `${CELL}px`, height: `${CELL}px` }} // <- **50px locked**
              onDragOver={(e) => isInput && e.preventDefault()}
              onDrop={(e) => isInput && onDrop(r, c, e)}
              onClick={() => onCellClick(r, c)}
            >
              {isInput ? (
                <input
                  className="mcw-inputbox"
                  inputMode="numeric"
                  value={token === "_" ? "" : token}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\s+/g, "");
                    if (v === "") onSet(r, c, "_");
                    else if (/^[0-9+\-×÷]+$/.test(v)) onSet(r, c, v);
                  }}
                />
              ) : (
                <span>{token}</span>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

function Toolbox({ toolbox, usedBag, singleUse, onPick }) {
  const Tile = ({ v }) => {
    const used = usedBag[v] ?? 0;
    const disabled = singleUse && used >= 99;
    return (
      <div
        className={`mcw-tile ${disabled ? "mcw-disabled" : ""}`}
        draggable={!disabled}
        onDragStart={(e) => e.dataTransfer.setData("text/plain", v)}
        onClick={() => onPick(v)}
        title="Drag or tap to use"
      >
        {v}
      </div>
    );
  };

  return (
    <div className="mcw-toolbox">
      <div className="mcw-tools-group">
        <div className="mcw-tools-title">Operators</div>
        <div className="mcw-tools-row">
          {toolbox.operators.map((o) => <Tile key={`op-${o}`} v={o} />)}
        </div>
      </div>
      <div className="mcw-tools-group">
        <div className="mcw-tools-title">Numbers</div>
        <div className="mcw-tools-row">
          {toolbox.numbers.map((n) => <Tile key={`n-${n}`} v={n} />)}
        </div>
      </div>
    </div>
  );
}

function FooterStatus({ totals, solved }) {
  return (
    <div className="mcw-status">
      {solved ? (
        <div className="mcw-success">🎉 All correct! Great job.</div>
      ) : (
        <div className="mcw-progress">Filled {totals.filled}/{totals.total} cells — keep going!</div>
      )}
      <Styles />
    </div>
  );
}

/* ---------------- styles ---------------- */

function Styles() {
  return (
    <style>{`
.mcw { overflow-x: hidden; font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; }
.mcw-header { display:flex; align-items:center; gap:12px; margin-bottom:12px; }
.mcw-btn { padding:6px 12px; border-radius:8px; border:1px solid #ddd; background:#fff; cursor:pointer; }
.mcw-lives { margin-left:auto; font-weight:600; }
.mcw-badge { background:#f33; color:#fff; padding:2px 8px; border-radius:999px; margin-right:6px; }

.mcw-wrap { display:flex; gap:16px; align-items:flex-start; flex-wrap:wrap; }

.mcw-grid { display:grid; gap:6px; } /* columns come inline; no CSS grid-template-columns here */

.mcw-cell {
  display:flex; align-items:center; justify-content:center;
  border:1px solid #d7deea; border-radius:8px; background:#fff;
  font-weight:600; font-size:18px;
}
.mcw-wall { visibility:hidden; }
.mcw-fixed { background:#f9fbff; }
.mcw-op { background:#fff7e6; }
.mcw-eq { background:#eef9f1; }

.mcw-input .mcw-inputbox {
  width:100%; height:100%; border:none; outline:none; text-align:center;
  font-size:inherit; font-weight:700; background:transparent;
}

.mcw-ok { box-shadow: inset 0 0 0 2px #b7f0c0; }
.mcw-bad { box-shadow: inset 0 0 0 2px #ffc0c0; }

/* toolbox */
.mcw-toolbox { flex:1 1 300px; background:#fff; border:1px solid #e6e9f2; border-radius:12px; padding:12px; min-width:0; }
.mcw-tools-group + .mcw-tools-group { margin-top:12px; }
.mcw-tools-title { font-weight:700; margin-bottom:8px; color:#334; }
.mcw-tools-row { display:flex; flex-wrap:wrap; gap:10px; }
.mcw-tile { min-width:46px; height:46px; padding:8px 12px; display:flex; align-items:center; justify-content:center; border:1px solid #d7deea; border-radius:10px; cursor:grab; background:#fff; font-weight:700; transition:transform .05s; font-size:16px; }
.mcw-tile:active { transform:scale(.98); }
.mcw-disabled { opacity:.45; cursor:not-allowed; }

.mcw-status { margin-top:12px; }
.mcw-success { color:#0a7b44; font-weight:700; }
.mcw-progress { color:#556; }
`}</style>
  );
}

/* ---------------- sample levels ---------------- */

export const SAMPLE_LEVEL = {
  grid: [
    ["19", "-", "15", "=", "4"],
    ["50", "-", "_", "=", "20"],
    ["50", "÷", "_", "=", "2"],
    ["50", "*", "_", "=", "650"],
    ["_", "÷", "17", "=", "17"],
  ],
};

export const BIG_LEVEL = {
  grid: [
    [" ", " ", "19", "-", "15", "=", "4", " ", " "],
    [" ", " ", " ", " ", " ", " ", " ", " ", " "],
    ["5", "0", "-", "_", "=", "2", "0", " ", " "],
    [" ", " ", " ", " ", " ", " ", " ", " ", " "],
    ["3", " ", "+", " ", "5", " ", "=", " ", "8"],
  ],
};